/**
 * API 공통 유틸리티
 * 토큰 관리 및 기본 API 호출 함수
 */

import { auth } from "../../firebase";
import { BLOCKED_ACCOUNT_MESSAGE } from "../../utils/loginAuthErrors";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://viewrapi-876455037250.asia-northeast3.run.app";

export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, detail: unknown, fallbackMessage?: string) {
    const message = formatApiErrorMessage(status, detail, fallbackMessage);
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

const formatApiErrorMessage = (
  status: number,
  detail: unknown,
  fallbackMessage = "",
): string => {
  const detailValue =
    detail && typeof detail === "object" && "detail" in detail
      ? (detail as { detail?: unknown }).detail
      : detail;
  const messageValue =
    detail && typeof detail === "object" && "message" in detail
      ? (detail as { message?: unknown }).message
      : undefined;
  const content = detailValue ?? messageValue ?? fallbackMessage;
  const rendered =
    typeof content === "string"
      ? content
      : content != null
        ? JSON.stringify(content)
        : "";
  return rendered ? `HTTP error! status: ${status} - ${rendered}` : `HTTP error! status: ${status}`;
};

/**
 * Main API가 돌려준 `{"error": "..."}` 코드를 읽는다.
 *
 * FastAPI가 `HTTPException(detail=...)`을 다시 `detail`로 감싸므로 두 단계까지
 * 벗겨낸다. 메시지 문자열 매칭보다 이 코드가 정확하므로 분기에 우선 사용한다.
 * 코드를 못 찾으면 빈 문자열을 돌려준다.
 */
export const readApiErrorCode = (error: unknown): string => {
  if (!error || typeof error !== "object" || !("detail" in error)) return "";
  let payload: unknown = (error as { detail?: unknown }).detail;
  for (let depth = 0; depth < 2; depth += 1) {
    if (!payload || typeof payload !== "object") return "";
    if ("error" in payload || "code" in payload) {
      return String(
        (payload as { error?: unknown; code?: unknown }).error ??
          (payload as { code?: unknown }).code ??
          "",
      )
        .trim()
        .toLowerCase();
    }
    if (!("detail" in payload)) return "";
    payload = (payload as { detail?: unknown }).detail;
  }
  return "";
};

const readApiError = async (response: Response): Promise<ApiError> => {
  const cloned = response.clone();
  try {
    return new ApiError(response.status, await response.json());
  } catch (_) {
    const text = await cloned.text().catch(() => "");
    return new ApiError(response.status, undefined, text || undefined);
  }
};

/**
 * Standalone function for getting fresh Firebase token
 */
export const getFreshToken = async (): Promise<string | null> => {
  try {
    // Try to get fresh token from Firebase auth first
    if (auth.currentUser) {
      const freshToken = await auth.currentUser.getIdToken(true); // forceRefresh=true

      // Update storage with fresh token
      localStorage.setItem("firebaseToken", freshToken);
      return freshToken;
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.warn("⚠️ Firebase 토큰 새로고침 실패:", errorMessage);
  }

  // Fallback to stored token (may be expired)
  const token =
    localStorage.getItem("firebaseToken") ||
    sessionStorage.getItem("firebaseToken");

  return token || null;
};

/**
 * 항상 유효한 토큰을 보장하는 함수
 * Firebase Auth의 자동 토큰 갱신 기능을 활용하여 Refresh Token 기반으로 동작
 */
export const ensureValidToken = async (): Promise<string | null> => {
  try {
    if (auth.currentUser) {
      // Firebase가 자동으로 토큰을 관리하므로 항상 최신 토큰 반환
      // forceRefresh=false: 만료 전이면 캐시된 토큰 반환, 만료 직전이면 자동 갱신
      // Firebase는 내부적으로 Refresh Token을 사용하여 자동으로 토큰을 갱신함
      const token = await auth.currentUser.getIdToken(false);

      // 토큰을 localStorage에 저장 (자동 로그인 유지)
      localStorage.setItem("firebaseToken", token);
      sessionStorage.setItem("firebaseToken", token);

      return token;
    }

    // Firebase 사용자가 없지만 저장된 토큰이 있는 경우
    // Firebase가 세션을 복원할 수 있으므로 잠시 대기 후 재시도
    const storedToken =
      localStorage.getItem("firebaseToken") ||
      sessionStorage.getItem("firebaseToken");

    if (storedToken) {
      // Firebase의 onAuthStateChanged가 자동으로 세션을 복원할 때까지 대기
      // 여기서는 저장된 토큰을 반환하되, API 호출 시 401이 발생하면 재시도
      return storedToken;
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    console.warn("⚠️ Firebase 토큰 가져오기 실패:", errorMessage);
  }

  // Fallback to stored token (may be expired, but better than nothing)
  const token =
    localStorage.getItem("firebaseToken") ||
    sessionStorage.getItem("firebaseToken");

  // Never fabricate an Authorization value. Public endpoints, such as the
  // email/password precheck, intentionally run without a Firebase session;
  // protected callers must wait for an actual Firebase identity.
  return token || null;
};

/**
 * 사용자 차단 여부 확인
 */
const checkUserBlocked = async (token: string | null): Promise<boolean> => {
  if (!token) return false;
  try {
    const checkUrl = `${API_BASE_URL}/api/users/me`;
    const checkResponse = await fetch(checkUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (checkResponse.ok) {
      const userInfo = (await checkResponse.json()) as {
        user?: { isBlocked?: boolean };
        isBlocked?: boolean;
      };
      const isBlocked = userInfo?.user?.isBlocked || userInfo?.isBlocked;

      if (isBlocked) {
        // 차단 메시지를 sessionStorage에 저장
        sessionStorage.setItem(
          "loginToast",
          JSON.stringify({
            type: "error",
            message: BLOCKED_ACCOUNT_MESSAGE,
          })
        );

        // 로그인 페이지로 리디렉션
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return true;
      }
    }
  } catch (checkError) {
    console.error("❌ API Service: 차단 확인 실패:", checkError);
  }
  return false;
};

/**
 * 기본 fetch 함수 래퍼
 */
export const apiCall = async (
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<unknown> => {
  if (options.signal?.aborted) throw new DOMException("Request cancelled", "AbortError");
  const url = `${API_BASE_URL}${endpoint}`;

  const token = isRetry
    ? await getFreshToken()
    : await ensureValidToken();

  if (options.signal?.aborted) throw new DOMException("Request cancelled", "AbortError");
  const { headers: externalHeaders, ...restOptions } = options;
  const safeHeaders = extractSafeHeaders(externalHeaders);

  const defaultOptions: RequestInit = {
    method: "GET",
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...safeHeaders,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };

  try {
    const response = await fetch(url, defaultOptions);

    if (options.signal?.aborted) throw new DOMException("Request cancelled", "AbortError");
    if (!response.ok) {
      // 401 오류 처리 - 토큰 새로고침 후 재시도
      if (response.status === 401 && !isRetry) {
        // Check if user is blocked
        const isBlocked = await checkUserBlocked(token);
        if (isBlocked) {
          throw new Error(
            `HTTP error! status: ${response.status} - Account blocked`
          );
        }

        // Firebase 사용자가 있으면 강제로 토큰 갱신 시도
        if (auth.currentUser) {
          try {
            const refreshedToken = await auth.currentUser.getIdToken(true); // forceRefresh=true

            // 새 토큰 저장 (자동 로그인 유지)
            localStorage.setItem("firebaseToken", refreshedToken);
            sessionStorage.setItem("firebaseToken", refreshedToken);
          } catch (refreshError) {
            console.error("❌ 토큰 갱신 실패:", refreshError);
            // 토큰 갱신 실패 시에도 재시도는 계속 진행
          }
        }

        // Retry with fresh token
        return await apiCall(endpoint, options, true);
      }

      throw await readApiError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (!options.signal?.aborted) console.error("API 호출 에러:", error);
    throw error;
  }
};

/**
 * Call a deliberately unauthenticated endpoint. Stored Firebase/Guest tokens
 * must not change an Email-first request into an authenticated claim.
 */
export const publicApiCall = async (
  endpoint: string,
  options: RequestInit = {},
): Promise<unknown> => {
  const { headers: externalHeaders, ...restOptions } = options;
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "GET",
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...extractSafeHeaders(externalHeaders),
    },
  });
  if (!response.ok) throw await readApiError(response);
  return await response.json();
};

/**
 * Generic fetch method for direct API calls with automatic token inclusion
 */
export const apiFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const url = `${API_BASE_URL}${endpoint}`;

  const token = await ensureValidToken();

  const { headers: externalHeaders, ...restOptions } = options;
  const safeHeaders = extractSafeHeaders(externalHeaders);

  const defaultOptions: RequestInit = {
    method: "GET",
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...safeHeaders,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };

  try {
    const response = await fetch(url, defaultOptions);

    // 401 응답 시 차단 여부 확인 (순환 참조 방지를 위해 직접 API 호출)
    if (response.status === 401) {
      await checkUserBlocked(token);
    }

    return response;
  } catch (error) {
    console.error("API Service fetch 에러:", error);
    throw error;
  }
};

/**
 * POST 요청 헬퍼
 */
export const postData = async (
  endpoint: string,
  data: unknown,
  userToken: string | null = null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  return await apiCall(endpoint, options);
};

/**
 * PUT 요청 헬퍼
 */
export const putData = async (
  endpoint: string,
  data: unknown,
  userToken: string | null = null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "PUT",
    body: JSON.stringify(data),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  return await apiCall(endpoint, options);
};

/**
 * DELETE 요청 헬퍼
 */
export const deleteData = async (
  endpoint: string,
  userToken: string | null = null,
  body: unknown = null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "DELETE",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  if (body) {
    options.body = JSON.stringify(body);
  }

  return await apiCall(endpoint, options);
};

/**
 * 인증된 API 호출 헬퍼
 */
export const authenticatedCall = async (
  endpoint: string,
  options: RequestInit = {},
  userToken: string | null = null
): Promise<unknown> => {
  const authenticatedOptions: RequestInit = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };

  return await apiCall(endpoint, authenticatedOptions);
};

/**
 * 외부에서 전달된 headers에서 Authorization을 제거하고 안전한 Record로 변환
 * apiCall/apiFetch가 내부적으로 토큰을 관리하므로, 외부 Authorization은 무시해야 함
 */
const extractSafeHeaders = (
  headers: HeadersInit | undefined
): Record<string, string> => {
  let headersObj: Record<string, string> = {};

  if (!headers) return headersObj;

  if (headers instanceof Headers) {
    headersObj = Object.fromEntries(headers.entries());
  } else if (Array.isArray(headers)) {
    headersObj = Object.fromEntries(headers);
  } else {
    headersObj = { ...(headers as Record<string, string>) };
  }

  const { Authorization: _, authorization: __, ...safe } = headersObj;
  return safe;
};

export { API_BASE_URL };
